// Draft of the project's inspection template in "הגדרות מסירות" (H-Checklist): the stored items plus
// the user's edits, saved together. Items are deactivated, never deleted.
import {
  INSPECTION_LIMITS,
  type InspectionTemplateResponse,
  type InspectionTemplateSaveRequest,
} from '@sivan/shared';

export interface TemplateRow {
  /** Stable key in the editor; equals `id` for stored items. */
  readonly key: string;
  readonly id: string | null;
  readonly areaId: string;
  readonly workCategoryId: string;
  /** The physical component the item checks (DR-189); null: none. */
  readonly componentId: string | null;
  readonly name: string;
  readonly active: boolean;
  /** Apartment model types, comma separated as typed; empty: every apartment. */
  readonly modelTypes: string;
}

export type TemplateRowErrors = Partial<Record<'name' | 'modelTypes', string>>;

let counter = 0;
const newKey = (): string => {
  counter += 1;
  return `new-${String(counter)}`;
};

export function rowsFrom(template: InspectionTemplateResponse): TemplateRow[] {
  return template.items.map((item) => ({
    key: item.id,
    id: item.id,
    areaId: item.areaId,
    workCategoryId: item.workCategoryId,
    componentId: item.componentId,
    name: item.name,
    active: item.active,
    modelTypes: item.modelTypes.join(', '),
  }));
}

/** "4 חדרים, פנטהאוז" → ["4 חדרים", "פנטהאוז"] (trimmed, without blanks or repeats). */
export const parseModelTypes = (text: string): string[] => [
  ...new Set(
    text
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t !== ''),
  ),
];

export function toRequest(
  rows: readonly TemplateRow[],
  listVersion: string,
): InspectionTemplateSaveRequest {
  return {
    listVersion,
    items: rows.map((row) => ({
      id: row.id,
      areaId: row.areaId,
      workCategoryId: row.workCategoryId,
      componentId: row.componentId,
      name: row.name.trim(),
      active: row.active,
      modelTypes: parseModelTypes(row.modelTypes),
    })),
  };
}

export function isDirty(rows: readonly TemplateRow[], template: InspectionTemplateResponse) {
  return (
    JSON.stringify(toRequest(rows, template.listVersion)) !==
    JSON.stringify(toRequest(rowsFrom(template), template.listVersion))
  );
}

/** Errors per row key; empty when the template can be saved. */
export function validateRows(rows: readonly TemplateRow[]): Record<string, TemplateRowErrors> {
  const errors: Record<string, TemplateRowErrors> = {};
  const seen = new Set<string>();
  for (const row of rows) {
    const e: TemplateRowErrors = {};
    const name = row.name.trim();
    const key = `${row.areaId}/${row.workCategoryId}/${name.toLowerCase()}`;
    if (name === '') e.name = 'שדה חובה';
    else if (name.length > INSPECTION_LIMITS.itemName)
      e.name = `עד ${String(INSPECTION_LIMITS.itemName)} תווים`;
    else if (seen.has(key)) e.name = 'הפריט כבר קיים בתחום זה בחדר';
    seen.add(key);
    const types = parseModelTypes(row.modelTypes);
    if (types.length > INSPECTION_LIMITS.modelTypesPerItem)
      e.modelTypes = `עד ${String(INSPECTION_LIMITS.modelTypesPerItem)} טיפוסי דירה`;
    else if (types.some((t) => t.length > INSPECTION_LIMITS.modelType))
      e.modelTypes = `סוג דירה עד ${String(INSPECTION_LIMITS.modelType)} תווים`;
    if (Object.keys(e).length > 0) errors[row.key] = e;
  }
  return errors;
}

/** A new item, placed after the last item of its area and work category (or of its area). */
export function addRow(
  rows: readonly TemplateRow[],
  areaId: string,
  workCategoryId: string,
  name: string,
): { rows: TemplateRow[]; key: string } {
  const row: TemplateRow = {
    key: newKey(),
    id: null,
    areaId,
    workCategoryId,
    componentId: null,
    name,
    active: true,
    modelTypes: '',
  };
  const lastOf = (match: (r: TemplateRow) => boolean) =>
    rows.reduce((last, r, index) => (match(r) ? index : last), -1);
  const sameGroup = lastOf((r) => r.areaId === areaId && r.workCategoryId === workCategoryId);
  const at = sameGroup >= 0 ? sameGroup : lastOf((r) => r.areaId === areaId);
  const next = [...rows];
  next.splice(at < 0 ? rows.length : at + 1, 0, row);
  return { rows: next, key: row.key };
}

/** Moves an item up (-1) or down (+1) among the items of its area and work category. */
export function moveRow(rows: readonly TemplateRow[], key: string, delta: -1 | 1): TemplateRow[] {
  const index = rows.findIndex((r) => r.key === key);
  const row = rows[index];
  if (row === undefined) return [...rows];
  const peers = rows
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => r.areaId === row.areaId && r.workCategoryId === row.workCategoryId);
  const position = peers.findIndex(({ i }) => i === index);
  const target = peers[position + delta];
  if (target === undefined) return [...rows];
  const next = [...rows];
  next[index] = target.r;
  next[target.i] = row;
  return next;
}
